import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  Select,
  SelectContent,
  SelectData,
  SelectItem,
  SelectTrigger,
  SelectType,
  SelectValue,
  SelectWithSearch,
  Textarea,
} from '@ais-ui/ui-core';
import React, { useMemo } from 'react';
import { UseFormReturn } from 'react-hook-form';
import { useLocalization } from '@ais-ui/locale-management';
import { SendMessageFormValues } from './sendMessageForm';
import { useFetchPSSWithType, useGetUserMmsi } from '@ais-ui/api';
import { ShipSelector } from '../../utils/ShipCommand';

interface SendMessageFormProps {
  form: UseFormReturn<SendMessageFormValues>;
}

export const SendMessageForm = ({ form }: SendMessageFormProps) => {
  const { messages } = useLocalization();

  const { data: fromMmsis } = useGetUserMmsi();
  const { data: pss } = useFetchPSSWithType('aton');

  const fromMMSIs: SelectType[] = useMemo(() => {
    if (!fromMmsis || !fromMmsis.records) {
      return [];
    }

    return fromMmsis.records.map(
      (fromMmsi) =>
        ({
          label: fromMmsi?.name ? fromMmsi.name.toString() : '',
          value: fromMmsi?.name ? fromMmsi.name.toString() : '',
        }) as SelectType,
    );
  }, [fromMmsis]);

  const allowedPSS: SelectData[] = useMemo(() => {
    if (!pss || !pss.records) {
      return [];
    }

    return pss.records.flatMap((pssItem) => {
      if (
        pssItem.allowed !== true ||
        pssItem.uuid == null ||
        pssItem.name == null
      ) {
        return [];
      }

      return [{ id: pssItem.uuid, name: pssItem.name }];
    });
  }, [pss]);

  return (
    <div className="space-y-8">
      <FormField
        control={form.control}
        name="type"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Type *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={(value) => {
                  field.onChange(value);
                  if (value === 'broadcast') {
                    form.setValue('receiver', null);
                  }
                }}
                value={field.value}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Choose message type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem
                    key="addressed"
                    value="addressed"
                    id="select-addressed-button"
                  >
                    Addressed
                  </SelectItem>
                  <SelectItem
                    key="broadcast"
                    value="broadcast"
                    id="select-broadcast-button"
                  >
                    Broadcast
                  </SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      <FormField
        control={form.control}
        name="sender"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>Sender MMSI *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select
                onValueChange={field.onChange}
                value={field.value}
                defaultValue=""
              >
                <SelectTrigger className="w-full">
                  <SelectValue
                    id="virtual-aton-chained-pss-select"
                    defaultValue=""
                    placeholder="Select MMSI to send from"
                  />
                </SelectTrigger>
                <SelectContent>
                  {fromMMSIs.map((option) => (
                    <SelectItem
                      key={option.value}
                      value={option.value}
                      id={
                        form.watch('type') === 'broadcast'
                          ? `sender-mmsi-${option.value}-button`
                          : undefined
                      }
                    >
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {form.getValues('type') === 'addressed' && (
        <FormField
          control={form.control}
          name="receiver"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>Receiver MMSI *</FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <ShipSelector
                  value={field.value}
                  onChange={field.onChange}
                ></ShipSelector>
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      <FormField
        control={form.control}
        name="message"
        render={({ field }) => {
          const messageType = form.watch('type');
          const maxLength = messageType === 'addressed' ? 85 : 90;

          return (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>Message *</FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Textarea
                  placeholder={messages.sendMessageFormMessagePlaceholder}
                  id="send-message-message"
                  {...field}
                  maxLength={maxLength}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          );
        }}
      />
      <FormField
        control={form.control}
        name="pss"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>PSS *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <SelectWithSearch
                value={field.value ?? undefined}
                onChange={(value) => field.onChange(value ?? '')}
                data={allowedPSS}
                placeholder="Select PSS"
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
    </div>
  );
};
